import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { UsersThree, UserPlus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROLE_COLORS = { publisher_owner: '#F59E0B', publisher_manager: '#00F0FF', publisher_content_manager: '#10B981', publisher_marketing_manager: '#EC4899' };
const INVITE_ROLES = ['publisher_manager', 'publisher_content_manager', 'publisher_marketing_manager'];

export default function PublisherTeam() {
  const { publisherId } = useParams();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: '', role_code: 'publisher_manager' });

  const fetch = () => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/team`, { withCredentials: true })
      .then(r => setTeam(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(fetch, [publisherId]);

  const invite = async () => {
    if (!form.email) { toast.error('Email required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/team/invite`, form, { withCredentials: true });
      toast.success(`Invitation sent to ${form.email}`);
      setShowInvite(false); setForm({ email: '', role_code: 'publisher_manager' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (membershipId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API_URL}/api/publisher-portal/${publisherId}/team/${membershipId}`, { withCredentials: true });
      toast.success('Member removed'); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#F59E0B]/50 focus:outline-none placeholder-[#444]";

  return (
    <div data-testid="publisher-team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Team</h1>
          <p className="text-sm text-[#666] mt-1">{team.length} members</p>
        </div>
        <button onClick={() => setShowInvite(!showInvite)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/[0.08] text-[#888] text-sm hover:text-white transition-colors"
          data-testid="invite-team-btn">
          <UserPlus size={16} /> Invite
        </button>
      </div>

      {showInvite && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-3">
          <p className="text-sm text-white font-semibold">Invite Team Member</p>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} type="email" placeholder="Email address" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
            <select className={inputCls} value={form.role_code} onChange={e => setForm({ ...form, role_code: e.target.value })}>
              {INVITE_ROLES.map(r => (
                <option key={r} value={r}>{r.replace(/publisher_/g, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={invite} className="px-5 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm"
              data-testid="send-invite-btn">Send Invitation</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading ? [...Array(2)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />) :
        team.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-8 text-center">
            <UsersThree size={40} className="mx-auto text-[#333] mb-3" />
            <p className="text-white font-medium">No Team Members</p>
          </div>
        ) : team.map(m => (
          <div key={m.user_id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center text-sm text-[#F59E0B] font-bold">
                {(m.name || '?')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{m.name} <span className="text-[#555]">@{m.username}</span></p>
                <p className="text-[11px] text-[#555]">{m.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {m.roles.map(r => (
                <span key={r} className="text-[10px] px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: `${ROLE_COLORS[r] || '#555'}15`, color: ROLE_COLORS[r] || '#555' }}>
                  {r.replace(/publisher_/g, '').replace(/_/g, ' ')}
                </span>
              ))}
              {!m.roles.includes('publisher_owner') && (
                <button onClick={() => remove(m.membership_id)} className="text-[#444] hover:text-[#EF4444] ml-2 transition-colors">
                  <Trash size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
